import { Injectable } from "@angular/core";
import { OnlineCreditService } from "./online-credit.service";
import { distinctUntilChanged, map, tap } from "rxjs/operators";
import { Observable, Subject, BehaviorSubject } from "rxjs";
import {
  Step,
  Steps,
  STEPS_WITH_COMPONENTS
} from "@pages/new-product/new-credit/models/step.model";
import { AlertController } from '@ionic/angular';
import { StoreService } from "@pages/new-product/new-credit/store/store.service";
import {
  ApplicationOnCredit,
  ApplicationStatus
} from './online-credit.model';
import { Router } from '@angular/router';
import { AlertService } from "@core/services/alert/alert.service";
import { NavigationService } from "@core/services/navigation/navigation.service";

@Injectable()

export class StepManagerService {
  private nextStep = new Subject<Step>();
  public stepChanged$ = this.nextStep.asObservable();
  private offlineAlert = {
    header: "Ваша заявка отправлена в выбранный офис",
    subHeader: "Для принятия решения по Вашей заявке необходима дополнительная информация. С Вами свяжется сотрудник банка",
    buttons: [
      {
        text: "Закрыть", role: "cancel", handler: () => {
          this.goToRoot();
        }
      },
    ],
  };
  private errorAlert = {
    header: 'Внимание!',
    message: `Произошла ошибка, попробуйте воспользоваться услугой позже.`,
    buttons: [
      {
        text: 'Закрыть', role: 'cancel', handler: () => {
          this.goToRoot()
        }
      },
    ],
    enableBackdropDismiss: false
  }
  private abortAlert = {
    header: 'Внимание!',
    message: `Вы действительно хотите отменить заявку?`,
    buttons: [
      {
        text: 'Да', handler: () => {
          this.setStepLoaded(false);
          if (this.store.value.application.status !== 'approved') {
            this.onlineCreditService.cancelApplication(this.store.value.application.id)
              .subscribe(
                () => {
                  this.store.clear();
                  this.setStepLoaded(true);
                  this.goToRoot();
                },
                (error) => {
                  const message = error.errorText || (error.error && error.error.errorText);
                  this.alertService.showInfoAlert(message || error);
                  this.setStepLoaded(true);
                })
          } else {
            this.onlineCreditService.clientDeny(this.store.value.application.id)
              .subscribe(
                () => {
                  this.store.clear();
                  this.setStepLoaded(true);
                  this.goToRoot();
                },
                (error) => {
                  const message = error.errorText || (error.error && error.error.errorText);
                  this.alertService.showInfoAlert(message || error);
                  this.setStepLoaded(true);
                })
          }
        }
      },
      {text: 'Нет', role: 'cancel'}
    ],
    enableBackdropDismiss: false
  }
  private stepLoaded = new BehaviorSubject<boolean>(false);
  public stepLoaded$ = this.stepLoaded.asObservable()
    .pipe(
      distinctUntilChanged(),
      tap((value) => {
        if (value) this.loaderText.next('');
      })
    );

  private loaderText = new BehaviorSubject('');
  public loaderText$ = this.loaderText.asObservable();
  activeStep: string;

  set loaderTitle(value) {
    this.loaderText.next(value);
  }

  constructor(
    private readonly onlineCreditService: OnlineCreditService,
    private alertController: AlertController,
    private alertService: AlertService,
    private router: Router,
    private readonly store: StoreService,
    private navigationService: NavigationService
  ) {
  }

  getInitialStep(): Observable<any> {
    return this.onlineCreditService.getApplicationData()
      .pipe(
        map((data: ApplicationOnCredit) => {
          if (data) {
            this.store.updateApplication(data);
            if (data.status === ApplicationStatus.Approved) {
              return this.findStep(Steps.calculation);
            }
            if (data.status === ApplicationStatus.Send) {
              const documentsSignStatus = data.onlineCreditDocuments.map((doc) => doc.isSigned);
              if (!documentsSignStatus.length || documentsSignStatus.includes(false)) {
                return this.findStep(Steps.signFinalDocumentPack);
              }
              return this.findStep(Steps.loanWasIssued);
            }
            if (data.status === ApplicationStatus.ConclusionOfContract) {
              return this.findStep(Steps.loanWasIssued);
            }
            if (data.status === ApplicationStatus.New) {
              return this.findStep(Steps.personalData);
            }
            if (data.status === ApplicationStatus.PersonalInfo) {
              return this.findStep(Steps.choiceOffice);
            }
            if (data.status === ApplicationStatus.ChoiceOffice) {
              return this.findStep(Steps.accountConfirmation);
            }
            if (data.status === ApplicationStatus.PublicServices) {
              return this.findStep(Steps.accountConfirmation);
            }
            if (data.status === ApplicationStatus.ConfirmPublicServices) {
              return this.findStep(Steps.signAgreements);
            }
            if (data.status === ApplicationStatus.ProcessedBki) {
              return this.findStep(Steps.applicationConfirmation)
            }
            if ([ApplicationStatus.Consent, ApplicationStatus.FullApplication].includes(data.status)) {
              return {
                ...this.findStep(Steps.personalData),
                injectableData: {
                  fillAdditionalPersonalData: true
                }
              }
            }
            return this.findStep(Steps.applicationSending);
          } else {
            // this.store.clear();
            return this.findStep(Steps.calculation);
          }
        })
      );
  }

  setStepLoaded(loading: boolean) {
    if (loading === this.stepLoaded.value) {
      return;
    }
    this.stepLoaded.next(loading);
  }

  changeStep(step: Steps, injectableData?: any) {
    const foundedStep = this.findStep(step);
    if (foundedStep && injectableData) {
      foundedStep.injectableData = {
        ...injectableData
      }
    }

    this.nextStep.next(foundedStep);
  }

  public showOfflineAlert() {
    this.setStepLoaded(false);
    this.alertController.create(this.offlineAlert).then((alert) => alert.present());
  }

  public showAbortAlert() {
    this.alertController.create(this.abortAlert).then((alert) => alert.present());
  }

  public showErrorStepAlert(err?: string, title: string = 'Внимание!') {
    if (typeof err === 'string') {
      const alert = {
        title,
        message: `${err}`,
        buttons: [
          {
            text: 'Закрыть', role: 'cancel', handler: () => {
              this.goToRoot();
            }
          },
        ],
        enableBackdropDismiss: true
      }
      let alerted = this.alertController.create(alert).then((alert) => {
        void alert.onDidDismiss().then(() => this.setStepLoaded(true));
        void alert.present()
      });
    }
    let alerted = this.alertController.create(this.errorAlert).then((alert) => {
      void alert.onDidDismiss().then(() => this.setStepLoaded(true));
      void alert.present()
    });
  }

  private findStep(name: Steps, injectableData?: any) {
    return STEPS_WITH_COMPONENTS.find(step => step.name === name)
  }

  goToRoot() {
    void this.navigationService.setRoot('/main')
    this.setStepLoaded(true)
  }
}
